import { resolveCodDisplayCurrency } from '@modules/CODPayments/utils/resolveCurrencyFromApi';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { formatDateWithTime } from '@shared/utils/FormatUtils';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PayoutItem } from '../models';

const FIELD_TO_API_COLUMN: Record<string, string> = {
  cod_payout_id: 'cod_payout.id',
  'client.name': 'client.name',
  'order.ref': 'order.ref',
  order_id: 'order_id',
  'order.tracking_number': 'order.tracking_number',
  'order.shop.name': 'order.shop.name',
  'order.courier.name': 'order.courier.name',
  payout_type: 'payout_type',
  amount: 'amount',
  currency: 'currency',
  created_at: 'created_at',
};

const VALID_EXPORT_COLUMNS = new Set([
  'id',
  'amount',
  'currency',
  'payout_type',
  'created_at',
  'client.name',
  'order.ref',
  'order_id',
  'order.tracking_number',
  'order.shop.name',
  'order.courier.name',
  'cod_payout.id',
  'cod_payout.total_amount',
]);

export const useAgGridColumns = () => {
  const { t } = useTranslation('codPayoutItems');
  const navigate = useNavigate();
  const teamCurrency = useCurrency();

  const columnDefs: ColDef<PayoutItem>[] = useMemo(
    () => [
      {
        field: 'cod_payout_id',
        headerName: t('table.payout', 'Liquidación'),
        minWidth: 120,
        sortable: false,
        valueGetter: (params) => params.data?.cod_payout_id ?? '-',
        cellRenderer: (params: any) => {
          const payoutId = params.data?.cod_payout_id;
          if (!payoutId) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <button
              onClick={() => navigate(`/cod-payments/payouts/${payoutId}`)}
              className="text-sm font-medium text-primary hover:underline text-left"
            >
              #{payoutId}
            </button>
          );
        },
      },
      {
        field: 'client.name',
        headerName: t('table.client', 'Cliente'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.client?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.client?.name ?? '-'}</span>
        ),
      },
      {
        field: 'order.ref',
        headerName: t('table.order', 'Pedido'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.ref ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium text-foreground">{params.data?.order?.ref ?? '-'}</span>
        ),
      },
      {
        field: 'order_id',
        headerName: t('table.order_id', 'ID Pedido'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params) => params.data?.order_id ?? params.data?.order?.id ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.order_id ?? params.data?.order?.id ?? '-'}</span>
        ),
      },
      {
        field: 'order.tracking_number',
        headerName: t('table.tracking', 'Tracking'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.tracking_number ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.order?.tracking_number ?? '-'}</span>
        ),
      },
      {
        field: 'order.shop.name',
        headerName: t('table.shop', 'Tienda'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.order?.shop?.name ?? '-'}</span>
        ),
      },
      {
        field: 'order.courier.name',
        headerName: t('table.courier', 'Mensajería'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.courier?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.order?.courier?.name ?? '-'}</span>
        ),
      },
      {
        field: 'payout_type',
        headerName: t('table.type', 'Tipo'),
        minWidth: 130,
        sortable: false,
        valueGetter: (params) => params.data?.payout_type ?? '-',
        cellRenderer: (params: any) => {
          const type = params.data?.payout_type;
          if (!type) return '-';

          const variants: Record<string, { variant: any; label: string }> = {
            advance: {
              variant: 'default',
              label: t('type.advance', 'Anticipo'),
            },
            settlement: {
              variant: 'success',
              label: t('type.settlement', 'Liquidación'),
            },
            recovery: {
              variant: 'warning',
              label: t('type.recovery', 'Recuperación'),
            },
          };

          const typeInfo = variants[type] || { variant: 'default', label: type };

          return <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>;
        },
      },
      {
        field: 'amount',
        headerName: t('table.amount', 'Importe'),
        minWidth: 120,
        sortable: true,
        valueGetter: (params) => params.data?.amount ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.amount ?? 0;
          const row: PayoutItem | undefined = params.data;
          const currency = resolveCodDisplayCurrency(row?.currency, teamCurrency);
          const isNegative = amount < 0;

          return (
            <span className={`text-sm font-medium ${isNegative ? 'text-destructive' : 'text-foreground'}`}>
              {formatCurrencyAmount(amount, currency)}
            </span>
          );
        },
      },
      {
        field: 'created_at',
        headerName: t('table.created_at', 'Fecha Creación'),
        minWidth: 180,
        sortable: true,
        valueGetter: (params) => params.data?.created_at ?? '-',
        cellRenderer: (params: any) => {
          const date = params.data?.created_at;
          if (!date) return '-';
          return <span className="text-sm text-muted-foreground">{formatDateWithTime(date)}</span>;
        },
      },
    ],
    [t, navigate, teamCurrency],
  );

  // 🎯 Función para obtener las columnas en el orden del frontend, filtrando las no válidas
  const getColumnsInOrder = useCallback((): string[] => {
    return columnDefs
      .map((col) => col.field as string | undefined)
      .filter((field): field is string => !!field)
      .map((field) => FIELD_TO_API_COLUMN[field] || field)
      .filter((column) => VALID_EXPORT_COLUMNS.has(column)); // Filtrar solo columnas válidas
  }, [columnDefs]);

  return { columnDefs, getColumnsInOrder };
};
